import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchCollection, getVal } from '../lib/api';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    async function loadContact() {
      try {
        const data = await fetchCollection('kontakt_information');
        if (data.items && data.items.length > 0) {
          setContactInfo(data.items[0]);
        }
      } catch (err) {
        console.error('Kunne ikke hente kontaktinformation til Header:', err);
      }
    }
    loadContact();
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const navItems = [
    { label: 'Hjem', path: '/' },
    { label: 'Om os', path: '/om-os' },
    { label: 'Udlejning', path: '/udlejning' },
    { label: 'Telt udlejning', path: '/telt-udlejning' },
    { label: 'Priser', path: '/priser' },
    { label: 'Lejebetingelser', path: '/lejebetingelser' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => (path === '/' ? pathname === '/' : pathname.startsWith(path));

  return (
    <header className="relative z-50 border-b border-brand-orange/10 overflow-hidden">
      {/* Background Image with 60% opacity */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-40" 
        style={{ 
          backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/27b85900-9717-41d2-8622-634ea09330b6/telt-header.jpg')` 
        }}
      />
      {/* Light overlay to ensure text readability */}
      <div className="absolute inset-0 bg-white/40 backdrop-blur-xs" />

      <div className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-16 sm:px-6 sm:py-18 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/logo_6.png"
            alt="J F D Service"
            className="h-24 w-auto sm:h-28 lg:h-32"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Hovedmenu">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative text-sm font-semibold uppercase tracking-[0.18em] transition-colors after:absolute after:-bottom-2 after:left-0 after:h-px after:w-full after:origin-center after:scale-x-0 after:bg-brand-sky after:transition-transform hover:text-brand-sky hover:after:scale-x-100 ${isActive(item.path) ? 'text-brand-sky after:scale-x-100' : 'text-slate-700'}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          {contactInfo?.phone && (
            <a
              href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
              className="inline-flex items-center gap-2 rounded-full border border-brand-orange/20 bg-white/80 px-4 py-2 text-xs font-bold text-slate-800 transition-colors hover:border-brand-sky hover:text-brand-sky"
            >
              <i className="fa-solid fa-phone text-brand-orange" aria-hidden="true" />
              <span>{contactInfo.phone}</span>
            </a>
          )}
          <button
            type="button"
            onClick={() => setIsOpen((value) => !value)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-orange/20 bg-white text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.05)] transition-colors hover:border-brand-sky hover:text-brand-sky lg:hidden"
            aria-label={isOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={isOpen}
            aria-controls="site-menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-[1.05rem]`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {isOpen && (
        <div id="site-menu" className="fixed inset-0 h-[100dvh] w-screen z-50 flex flex-col bg-white lg:hidden overflow-y-auto">
          {/* Mobil Menu Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-5 sm:px-6 border-b border-brand-orange/5 shrink-0">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/logo_6.png"
                alt="J F D Service"
                className="h-24 w-auto sm:h-28"
              />
            </Link>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-orange/20 bg-white text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.05)] transition-colors hover:border-brand-sky hover:text-brand-sky"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-[1.05rem]" aria-hidden="true" />
            </button>
          </div>

          {/* Mobil Menu Indhold */}
          <div className="flex flex-col justify-between flex-grow shrink-0 px-4 py-8 sm:px-6">
            <nav className="grid gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between border-b border-brand-orange/10 py-3 font-display text-2xl leading-none tracking-[-0.02em] transition-colors sm:text-3xl ${isActive(item.path) ? 'text-slate-900' : 'text-slate-400 hover:text-slate-900'}`}
                >
                  <span>{item.label}</span>
                  <i className="fa-solid fa-arrow-right text-[0.7em] text-brand-sky" aria-hidden="true" />
                </Link>
              ))}
            </nav>

            <div className="mt-12 grid gap-4 border-t border-brand-orange/10 pt-6 text-sm text-slate-600 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-brand-sky">Kontakt</p>
                <a href="tel:22426011" className="mt-2 block text-lg font-medium text-slate-900 transition-colors hover:text-brand-sky">
                  22 42 60 11
                </a>
                <a href="mailto:J_danielsen85@hotmail.com" className="mt-1 block transition-colors hover:text-brand-sky">
                  J_danielsen85@hotmail.com
                </a>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-brand-sky">Adresse</p>
                <p className="mt-2 text-slate-900">Søllingevej 35, 5750 Ringe</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
